import type { SiteTourStep } from '../components/SiteTour';

export type TourId = 'site' | 'schedule' | 'patrol' | 'post' | 'payroll';

export interface TourDefinition {
  id: TourId;
  name: string;
  description: string;
  steps: SiteTourStep[];
  destination: { index: number; subIndex: number };
  expandSidebar?: boolean;
}

const siteTourSteps: SiteTourStep[] = [
  {
    eyebrow: 'Welcome',
    title: 'Welcome to OdinsEye',
    body: 'This is your security operations workspace. This short tour points out the main areas of the portal — it takes about a minute, and you can replay it anytime from Settings / Profile.',
  },
  { target: 'command-overview', eyebrow: 'At a glance', title: 'Your ops centre', body: 'Home is your operational starting point. It brings urgent alarms, missed shift starts, leave requests, recent reports, coverage gaps and expiring licences into one action-focused view.' },
  { target: 'command-priorities', eyebrow: 'Act now', title: 'Immediate priorities', body: 'Alarms, no-shows and leave requests are ordered into one queue. Select any row to open the right response workspace with its site, date or record ready.' },
  { target: 'command-coverage', eyebrow: 'Plan ahead', title: 'Coverage horizon', body: 'See uncovered and unpublished duties across the next 14 days. Selecting a gap opens that site and date directly on the Duty Board.' },
  { target: 'nav-schedule', eyebrow: 'Rostering', title: 'Schedule & rostering', body: 'Build and manage your roster. Use the Duty Board to create duties and assign guards, see the guard-facing roster under Shifts, and approve time off under Leave.' },
  { target: 'nav-command', eyebrow: 'Live ops', title: 'Ops Centre, live view & history', body: 'Return to Home for operational priorities, follow guard positions on the live map, or replay completed shifts — routes, checkpoint scans and events — under Shift History.' },
  { target: 'nav-alarms', eyebrow: 'Response', title: 'Alarms', body: 'Panic alerts raised from the field arrive here. Acknowledge and resolve them from the response queue — a badge on this item shows open alarms at a glance.' },
  { target: 'nav-staff', eyebrow: 'Compliance', title: 'Staff & licence verification', body: 'Your team directory: admins, managers and guards. Add a user to email them an invite, or share a join code, and open any profile for full details. The Licence Verification tab is where you review submitted security licences and approve or reject them.' },
  { target: 'nav-reports', eyebrow: 'Reporting', title: 'Reports', body: 'Incident reports from the field land here for triage and review, while Summary Reports gathers daily reports, patrol reports and visitor logs — all exportable.' },
  { target: 'nav-analytics', eyebrow: 'Insights', title: 'Analytics', body: 'Track performance and workload trends across sites, shifts and guards.' },
  { target: 'nav-sites', eyebrow: 'Patrols', title: 'Sites, posts & patrols', body: 'Set up client sites with geofences, define posts, and build patrol routes with checkpoints for guards to scan on their rounds. Patrol creation starts here.' },
  { target: 'nav-payroll', eyebrow: 'Pay preparation', title: 'Payroll', body: 'Turn clocked shifts into reviewed employee timesheets, manage pay bands, lock approved periods and export frozen gross-pay figures for your payroll provider.' },
  { target: 'nav-admin', roles: ['admin'], eyebrow: 'Administration', title: 'Admin', body: 'Onboard clients, give them portal access and manage company administrator accounts.' },
  { target: 'notifications', eyebrow: 'Updates', title: 'Notifications', body: 'Licence submissions, report updates and alarms land here, so nothing slips past you.' },
  { target: 'nav-settings', eyebrow: 'Preferences', title: 'Settings & profile', body: 'Your account details and workspace preferences live here — including the option to replay this tour whenever you like.' },
  { eyebrow: 'All set', title: 'You’re ready to go', body: 'A good first step: create a site, add your staff, then build your first roster on the Duty Board. Replay this tour anytime from Settings / Profile.' },
];

const scheduleTourSteps: SiteTourStep[] = [
  { eyebrow: 'Duty Board', title: 'Build a guard roster', body: 'Use the Duty Board to plan coverage for each client site, assign eligible guards, and publish shifts to the guard app.' },
  { target: 'schedule-filters', eyebrow: 'Choose the scope', title: 'Client, site and date', body: 'Choose a client and site, move between dates, and switch between the detailed day view and the weekly overview.' },
  { target: 'schedule-status', eyebrow: 'Draft or live', title: 'Know what guards can see', body: 'The status explains whether this roster is still a draft, is live for guards, or has unpublished changes.' },
  { target: 'schedule-board', eyebrow: 'Plan coverage', title: 'Create duties and breaks', body: 'Add duty and break blocks, then drag or resize them on the timeline. Select a duty to edit its type, times, location, licence requirement, and staffing.' },
  { target: 'schedule-guards', eyebrow: 'Staff the roster', title: 'Assign eligible guards', body: 'Open the Guards panel to search your team. Drag an available guard onto a duty or break; licence, leave, and published-shift conflicts keep unavailable guards clearly separated.' },
  { target: 'schedule-publish', eyebrow: 'Go live', title: 'Publish when ready', body: 'Publishing makes assigned shifts visible to guards. If you edit a live roster, publish it again so guards receive the latest changes.' },
  { eyebrow: 'Ready to roster', title: 'Your schedule workflow', body: 'Choose the site and date, add coverage, assign guards, review warnings, then publish. Use Help on this page whenever you need a refresher.' },
];

const patrolTourSteps: SiteTourStep[] = [
  {
    eyebrow: 'Patrol Builder',
    title: 'Build a patrol route',
    body: 'A patrol is the route a guard follows. Choose a site, add the stops in order, decide where a QR scan is needed, then save. Some controls appear only after you choose a site or add a stop.',
  },
  {
    target: 'patrol-builder-site',
    eyebrow: 'Choose the place',
    title: 'Choose the client and site',
    body: 'Choose the client first, then the site. The green line on the map shows the site area. Edit bounds opens the site setup if that area needs changing.',
  },
  {
    target: 'patrol-builder-details',
    eyebrow: 'Patrol details',
    title: 'Name the patrol',
    body: 'Add a clear name and, if useful, notes for the guards. The suggested time is only a guide. Enter the planned number of minutes if you want a time saved with the patrol.',
  },
  {
    target: 'patrol-builder-route',
    eyebrow: 'Add stops',
    title: 'Draw a new stop or reuse one',
    body: 'After you choose a site, Draw and Reuse change the hint above the map. Use Draw when you want to click the map for a new stop. Use Reuse when you want to choose an orange checkpoint already saved at this site. Either kind of stop is added to the end of the route.',
  },
  {
    target: 'patrol-builder-map',
    eyebrow: 'Use the map',
    title: 'Place and move the route',
    body: 'After you choose a site, click the map to add a numbered stop. Drag a pin to move it. Select a stop in the list to change its settings. You can also select any map pin except the first. With two or more stops, the first pin switches the loop on or off. Right-click a pin to remove it. Use + and - to zoom.',
  },
  {
    target: 'patrol-builder-route',
    eyebrow: 'Visit order',
    title: 'Put the stops in the right order',
    body: 'After you add stops, the list shows the order guards follow. The first arrow moves a stop earlier, the second moves it later, and X removes it. Close loop joins the last stop back to the first. Undo removes the newest stop; Clear asks before removing every stop.',
  },
  {
    target: 'patrol-builder-checkpoint',
    eyebrow: 'Stop settings',
    title: 'Tell guards what to do here',
    body: 'After you add a stop, choose it to open this panel. Give it a clear label and checkpoint name; the code is optional. Radius sets the area used to record whether the guard scanned nearby. Require QR scan makes a scan compulsory. X closes the panel, while Remove stop takes it out of the route.',
  },
  {
    target: 'patrol-builder-library',
    eyebrow: 'Saved work',
    title: 'Reuse checkpoints and manage patrols',
    body: 'After you choose a site, Reusable checkpoints opens a list of saved points you can add. Existing patrols shows saved routes. Show previews a route, Edit loads it for changes, and QR opens its codes. Del opens a warning; Confirm permanently deletes the patrol and Cancel keeps it.',
  },
  {
    eyebrow: 'QR codes',
    title: 'Print signs for scan stops',
    body: 'For a saved patrol, QR includes only stops where Require QR scan is on. Download PDF saves a printable file, Print opens the print window, and X closes the QR window.',
  },
  {
    target: 'patrol-builder-actions',
    eyebrow: 'Save or leave',
    title: 'Know the top buttons',
    body: 'Help replays this tour. Save patrol stores a new route; Save changes updates a route you are editing. Cancel edit drops those unsaved edits. Back to Sites, or the OdinsEye logo, leaves the builder. If a leave warning appears, Stay and finish keeps the builder open and Leave anyway discards the draft.',
  },
  {
    eyebrow: 'Ready to patrol',
    title: 'Your patrol checklist',
    body: 'Choose the site, name the patrol, add the stops in order, check each stop and its QR rule, then save. You can return later to preview, edit, print QR codes or delete the patrol.',
  },
];

const postTourSteps: SiteTourStep[] = [
  {
    eyebrow: 'Post Builder',
    title: 'Create a fixed guard post',
    body: 'A post is a fixed place where a guard can be scheduled. Choose a site, name the post, place it on the map, then save. Some controls appear only after you choose a site or place a point.',
  },
  {
    target: 'post-builder-site',
    eyebrow: 'Choose the place',
    title: 'Choose the site',
    body: 'Choose the site this post belongs to. Its green site area and any saved posts will appear on the map.',
  },
  {
    target: 'post-builder-details',
    eyebrow: 'Post details',
    title: 'Name the post and set its area',
    body: 'Enter a clear post name. Notes are optional. Radius sets how large the area around the post should be. The location is filled in after you place the blue pin on the map.',
  },
  {
    target: 'post-builder-map',
    eyebrow: 'Use the map',
    title: 'Place the post',
    body: 'After you choose a site, click the map to add the blue pin, then drag it if it needs moving. Green shows the site area, orange shows saved posts, and blue is the new post.',
  },
  {
    target: 'post-builder-map',
    eyebrow: 'Map buttons',
    title: 'Move around and manage the draft',
    body: 'After you choose a site, use + and - to zoom. Clear point removes only the blue pin and keeps the details you typed. Once you place a point, Edit draft opens the right-hand panel. Save post works when the site, name and map point are ready; every Save post button does the same thing.',
  },
  {
    target: 'post-builder-draft',
    eyebrow: 'Draft panel',
    title: 'Check the new post',
    body: 'The right-hand panel opens after you place a point. It changes the same name, notes and radius. X hides the panel but keeps the point. Saving makes the post available for a fixed-post duty later; it does not schedule a guard by itself.',
  },
  {
    target: 'post-builder-library',
    eyebrow: 'Saved posts',
    title: 'View or remove an existing post',
    body: 'After you choose a site, Existing posts opens and closes the saved list. Select a row, View, or an orange map point to see its details. X closes the details. Del or Delete post opens a warning; Confirm permanently deletes the post and Cancel keeps it.',
  },
  {
    target: 'post-builder-actions',
    eyebrow: 'Save or leave',
    title: 'Know the top buttons',
    body: 'Help replays this tour. Clear point removes the new map pin, and Save post stores the post. Back to Sites, or the OdinsEye logo, leaves the builder. If a leave warning appears, Stay and finish keeps the builder open and Leave anyway discards the draft.',
  },
  {
    eyebrow: 'Post ready',
    title: 'Your post checklist',
    body: 'Choose the site, add the name and any notes, place the point, check the radius, then save. The post can then be chosen when you create a fixed-post duty on the Duty Board.',
  },
];

const payrollTourSteps: SiteTourStep[] = [
  {
    destination: { index: 11, subIndex: 1 },
    eyebrow: 'Payroll workflow',
    title: 'Prepare payroll with a clear audit trail',
    body: 'OdinsEye prepares gross pay for your payroll provider: configure effective-dated rates, calculate a pay period from clock records, review each employee, approve, lock, then export the frozen register.',
  },
  {
    destination: { index: 11, subIndex: 1 },
    target: 'payroll-workflow',
    expand: true,
    eyebrow: 'Use this order',
    title: 'Four controlled stages',
    body: 'This strip sits above every payroll tab, collapsed to one line until you open it. Each stage is a shortcut to where that work happens. Rate setup is admin-controlled; managers can calculate and review; only an admin can lock the approved period for export.',
  },
  {
    destination: { index: 11, subIndex: 2 },
    target: 'payroll-bands-list',
    roles: ['admin'],
    eyebrow: 'Rates',
    title: 'Use effective-dated pay bands',
    body: 'Create grades and schedule rate changes from their true effective date. Keeping the rate history intact lets an earlier pay period retain the rate that applied at the time.',
  },
  {
    destination: { index: 11, subIndex: 2 },
    target: 'payroll-band-assignments',
    roles: ['admin'],
    eyebrow: 'Employees',
    title: 'Assign every guard before calculating',
    body: 'Assignments carry the pay band, employment type, payroll reference and cost centre. Unassigned employees fall back to the company rate and are flagged for review.',
  },
  {
    destination: { index: 11, subIndex: 1 },
    target: 'payroll-timesheet-periods',
    eyebrow: 'Pay period',
    title: 'Create and select the period',
    body: 'Choose the period you are working on from the selector, or use New period to record fresh work dates. The bar keeps its stage, approval progress, outstanding decisions and employee pay date in view, and the full history sits under All pay periods at the foot of the page.',
  },
  {
    destination: { index: 11, subIndex: 1 },
    target: 'payroll-timesheet-recalculate',
    eyebrow: 'Calculate',
    title: 'Build timesheets from clock records',
    body: 'Recalculate reads clock-ins, clock-outs, pay bands, premiums, minimum shift pay, rounding and overtime rules. Only a complete unpaid-break start and end reduce payable time; missing, incomplete or skipped breaks stay payable and are flagged. Recalculation reopens reviewed figures so changed pay must be approved again.',
  },
  {
    destination: { index: 11, subIndex: 1 },
    target: 'payroll-timesheet-review',
    eyebrow: 'Review',
    title: 'Resolve exceptions employee by employee',
    body: 'Search for someone, or filter down to the sheets awaiting a decision or carrying flags. Opening a row gives you its pay lines, non-shift pay and audit trail on separate tabs, with site-local clock times. Check missing clocks, break exceptions, roster variances and fallback rates, then approve clean sheets, record an approval note for accepted exceptions, or return a sheet with a reason.',
  },
  {
    destination: { index: 11, subIndex: 1 },
    target: 'payroll-period-actions',
    eyebrow: 'Control',
    title: 'Advance one stage at a time',
    body: 'The period bar offers only the action the current stage allows: start review, approve every employee, approve the period, then have an admin lock it. Download the frozen register, and mark the period paid only after your payroll provider confirms completion.',
  },
  {
    destination: { index: 11, subIndex: 0 },
    target: 'payroll-preview-period',
    eyebrow: 'Optional preview',
    title: 'Model a live date window',
    body: 'Gross pay preview is a planning tool that reads current shift data. It does not create, approve or lock a pay period, so use Timesheets for the final hand-off.',
  },
  {
    destination: { index: 11, subIndex: 0 },
    target: 'payroll-preview-rules',
    expand: true,
    roles: ['admin'],
    eyebrow: 'Rules',
    title: 'Check the assumptions behind pay',
    body: 'The Payroll rules panel holds premiums, overtime, rounding, clock tolerances, minimum shift pay and leave accrual. It stays collapsed until you need it, and shows an unsaved badge while changes are pending. Validate configurable overtime thresholds against the applicable roster and employment terms, and save rule changes before downloading a preview CSV.',
  },
  {
    destination: { index: 11, subIndex: 0 },
    target: 'payroll-preview-generate',
    eyebrow: 'Reconcile',
    title: 'Treat preview warnings as work to do',
    body: 'Generate the preview, inspect warnings and totals, and expand employees to trace the figures. If inputs change, the result is marked out of date and downloads are disabled until it is regenerated.',
  },
  {
    destination: { index: 11, subIndex: 1 },
    eyebrow: 'Ready to run',
    title: 'Your payroll checklist',
    body: 'Rates assigned, clocks complete, exceptions reviewed, employee timesheets approved, period locked and frozen register downloaded. Your provider completes tax, PRSI, USC, net pay and payslips; then an admin records the period as paid.',
  },
];

export const tourDefinitions: Record<TourId, TourDefinition> = {
  site: { id: 'site', name: 'Portal overview', description: 'Main tools and navigation', steps: siteTourSteps, destination: { index: 0, subIndex: 2 }, expandSidebar: true },
  schedule: { id: 'schedule', name: 'Schedule & Duty Board', description: 'Creating, staffing and publishing rosters', steps: scheduleTourSteps, destination: { index: 5, subIndex: 7 } },
  patrol: { id: 'patrol', name: 'Patrol Builder', description: 'Routes, stops and QR checks', steps: patrolTourSteps, destination: { index: 7, subIndex: 0 } },
  post: { id: 'post', name: 'Post Builder', description: 'Fixed guard posts and map areas', steps: postTourSteps, destination: { index: 7.5, subIndex: 0 } },
  payroll: { id: 'payroll', name: 'Payroll workflow', description: 'Rates, review, approval, locking and export', steps: payrollTourSteps, destination: { index: 11, subIndex: 1 } },
};

export const availableTours = Object.values(tourDefinitions);
